//PropTypes
import PropTypes from "prop-types";
// React
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
// Third party
import countBy from "lodash-es/countBy";
// Helpers
import { getSanitatedLocationOEMs } from "@/Common/helpers/OEMs";
// MUI
import { Grid, Typography } from "@mui/material";

// Components
import MultipleTextFieldGroup from "@/Layouts/components/MultipleTextFieldGroup/MultipleTextFieldGroup";
import SimpleIconButton from "@/Layouts/components/Buttons/SimpleIconButton";
import SimpleDoubleChecking from "@/Layouts/components/Buttons/SimpleDoubleChecking";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// ICONS
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

// APIs
import { updateAdministrationTools } from "@/Settings/api/AdministrationTools/mutations";

/********************************************* HELPERS FUNCTIONS ************************************************/
function generalAccountLedger() {
	return {
		oemName: "",
		oemReference: "",
		prefixes: [""],
	};
}

function getNonSelectedOEMs(selectedOEMs, OEMs) {
	return OEMs.filter((oem) => !selectedOEMs.some((selectedOem) => oem.oemName === selectedOem?.oemName));
}

function isStringRepeated(array, string) {
	const count = countBy(array);
	return count[string] > 1;
}

function atMostOneEmptyElement(arr) {
	const emptyPrefixAmount = arr.reduce((acc, el) => {
		return acc + (el.length === 0 ? 1 : 0);
	}, 0);
	return emptyPrefixAmount <= 1;
}

export default function GeneralLedgerPrefix(props) {
	// Global State (Redux Store)
	const location = useSelector((state) => state.location.location);
	const locationId = location?.locationId;
	const oems = getSanitatedLocationOEMs(location?.oems || []);

	// Local State
	const [ledgers, setLedgers] = useState([]);
	const [isValidAllGeneralLedgers, setIsValidAllGeneralLedgers] = useState(true);

	/************************************************* MUTATIONS *************************************************/
	const { mutate, isPending: isLoading } = useMutation({
		mutationKey: ["generalLedgerPrefix", location],
		mutationFn: (input) => updateAdministrationTools(locationId, input),
	});

	/******************************************* HANDLERS FUNCTIONS ************************************************/
	const handleChange = (value, oemReference, prefixes) => {
		let updatedDMSGeneralLedgerPrefixes = [];

		updatedDMSGeneralLedgerPrefixes = ledgers.map((generalLedgerPrefix) => {
			return oemReference === generalLedgerPrefix.oemReference
				? {
						oemName: value.oemName,
						oemReference: value.oemReference,
						prefixes,
				  }
				: generalLedgerPrefix;
		});

		const input = {
			integrations: {
				...props?.settings?.integrations,
				generalLedgerPrefix: updatedDMSGeneralLedgerPrefixes,
			},
		};

		mutate(input);
		setIsValidAllGeneralLedgers(true);
	};

	// Function that handles when a whole new ledger is added
	const handleAddLedger = () => {
		const updatedLedgers = [...ledgers];
		updatedLedgers.push(generalAccountLedger());
		setLedgers(updatedLedgers);

		setIsValidAllGeneralLedgers(!updatedLedgers.some((generalLedger) => generalLedger.oemName === ""));
	};

	// Function that handles when a new input is added to any of the prefixes text group
	const handleOnAddPrefixInput = (_value, oemReference) => {
		const toUpdateGeneralLedgers = ledgers.map((ledger) =>
			ledger.oemReference === oemReference ? { ...ledger, prefixes: [...ledger.prefixes, ""] } : ledger
		);

		const areAllLedgersValid = toUpdateGeneralLedgers.map((ledger) => handleAreAllGeneralLedgerValid(ledger.prefixes));

		const prefixesToAdd = toUpdateGeneralLedgers.find((ledger) => ledger.oemReference === oemReference);
		if (atMostOneEmptyElement(prefixesToAdd.prefixes)) {
			const input = {
				integrations: {
					...props?.settings?.integrations,
					generalLedgerPrefix: toUpdateGeneralLedgers,
				},
			};
			mutate(input);
		}
		setLedgers(toUpdateGeneralLedgers);
		setIsValidAllGeneralLedgers(areAllLedgersValid.every((ledger) => ledger));
	};

	// Function that handles when any change occur in any of the inputs of the prefixes
	const handleActionUpdate = (value, oemReference, index) => {
		const toUpdateGeneralLedgers = ledgers.map((generalLedgerPrefix) => {
			return oemReference === generalLedgerPrefix.oemReference
				? {
						...generalLedgerPrefix,
						prefixes: generalLedgerPrefix.prefixes.map((prefix, idx) => (idx === index ? value : prefix)),
				  }
				: generalLedgerPrefix;
		});

		const areAllLedgersValid = toUpdateGeneralLedgers.map((generalLedger) => handleAreAllGeneralLedgerValid(generalLedger.prefixes));

		const input = {
			integrations: {
				...props?.settings?.integrations,
				generalLedgerPrefix: toUpdateGeneralLedgers,
			},
		};

		if (!areAllLedgersValid.every((ledger) => ledger)) {
			setLedgers(toUpdateGeneralLedgers);
			setIsValidAllGeneralLedgers(false);
		} else {
			mutate(input);
			setIsValidAllGeneralLedgers(areAllLedgersValid.every((ledger) => ledger));
		}
	};

	// Function that handles when a prefix is removed from its corresponding prefixes group
	const handleDelete = (index, oemReference) => {
		const toUpdateGeneralLedgers = ledgers.map((generalLedgerPrefix) => {
			return generalLedgerPrefix.oemReference === oemReference
				? {
						...generalLedgerPrefix,
						prefixes: generalLedgerPrefix.prefixes.filter((_, idx) => idx !== index),
				  }
				: generalLedgerPrefix;
		});

		const input = {
			integrations: {
				...props?.settings?.integrations,
				generalLedgerPrefix: toUpdateGeneralLedgers,
			},
		};

		setIsValidAllGeneralLedgers(true);
		mutate(input);
	};

	// Function that handles the validation of the inputs of the prefixes list and highlight the input as red if there's any error
	const handleSingleInputValidation = (value, prefixes) => value?.length && !isStringRepeated(prefixes, value);

	// Function that handles the validation of all the general ledger components
	const handleAreAllGeneralLedgerValid = (prefixes = []) => {
		const areNotRepeatedPrefixes = prefixes.length === [...new Set(prefixes)].length;
		const areAtMostOneEmptyPrefix = atMostOneEmptyElement(prefixes);

		return areNotRepeatedPrefixes && areAtMostOneEmptyPrefix;
	};

	// Function that handles the deletion of any general ledger component
	const handleDeleteGeneralLedger = (oemReference) => {
		const toUpdateGeneralLedgers = ledgers.filter((ledger) => ledger.oemReference !== oemReference);

		const input = {
			integrations: {
				...props?.settings?.integrations,
				generalLedgerPrefix: toUpdateGeneralLedgers,
			},
		};

		mutate(input);
	};

	useEffect(() => {
		if (!locationId) {
			return;
		}

		setLedgers(props.settings?.integrations?.generalLedgerPrefix || []);
	}, [props.settings, locationId]);

	return (
		<Grid item container direction="column" marginTop={2} xs={12} className="!my-[32px]">
			<Grid item container direction={"column"}>
				<Typography className="!font-montserrat !text-default !text-[20px] !font-medium">{props.title}</Typography>
				{ledgers?.map((ledger, idx) => {
					return (
						<Grid item key={ledger?._id || idx} container direction={"column"} mb={ledgers?.length - 1 !== idx ? 3 : 0}>
							<Grid item container direction={"row"} alignItems={"center"}>
								<Grid item mb={1}>
									<SmartSelector
										selectBy={"oemName"}
										showBy={"oemName"}
										options={getNonSelectedOEMs(ledgers, oems)}
										selected={ledger?.oemName ? { oemName: ledger.oemName, oemReference: ledger.oemReference } : null}
										inputProps={{
											placeholder: "Select Source",
										}}
										disabled={isLoading}
										sx={{ width: "220px" }}
										onChangeAction={(option) => handleChange(option, ledger.oemReference, ledger.prefixes)}
									/>
								</Grid>
								<Grid item mb={1}>
									<SimpleDoubleChecking
										className={"btn-delete"}
										color={"var(--gray-blue-color)"}
										confirmationButtonColor={"var(--gray-blue-color)"}
										icon={<CloseRoundedIcon sx={{ width: "30px" }} />}
										iconHover={<CloseRoundedIcon sx={{ width: "30px" }} />}
										confirmationIcon={<DeleteOutlineOutlinedIcon sx={{ width: "30px" }} />}
										onDeleteAction={() => handleDeleteGeneralLedger(ledger.oemReference)}
									/>
								</Grid>
							</Grid>
							<Grid item xs={12}>
								<MultipleTextFieldGroup
									sx={{ width: "100px", fontFamily: "PTSansProNarrow", fontSize: "20px" }}
									items={ledger?.prefixes}
									textFieldStyle={{ width: "100px" }}
									placeholder={`-`}
									onAdd={(value) => handleOnAddPrefixInput(value, ledger.oemReference)}
									onActionUpdate={(value, from, idx) => handleActionUpdate(value, ledger.oemReference, idx)}
									onDelete={(idx) => handleDelete(idx, ledger.oemReference)}
									simpleValidation={handleSingleInputValidation}
									validation={handleAreAllGeneralLedgerValid}
									deleting={isLoading}
									isLoading={isLoading}
									readOnly={isLoading}
									disabledAddButton={!isValidAllGeneralLedgers}
								/>
							</Grid>
						</Grid>
					);
				})}
				<Grid item mt={3} ml={12.15} justify="center" align="left">
					<SimpleIconButton
						className={"btn-add"}
						color={"var(--primary-color)"}
						icon={<AddCircleOutlineRoundedIcon sx={{ fontSize: "30px" }} />}
						iconHover={<AddCircleRounded sx={{ fontSize: "30px" }} />}
						disabled={ledgers?.length === oems.length || !isValidAllGeneralLedgers || !oems.length || isLoading}
						onClick={handleAddLedger}
					/>
				</Grid>
			</Grid>
		</Grid>
	);
}

GeneralLedgerPrefix.propTypes = {
	settings: PropTypes.object,
	title: PropTypes.string,
};
